import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  Building2Icon,
  CheckIcon,
  ClockIcon,
  PackageIcon,
  TruckIcon,
  WarehouseIcon,
  XIcon,
} from "lucide-react";
import { type ComponentType, useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { StockTransferDetail } from "@/modules/inventory/presentation/api/client";
import {
  useApproveStockTransfer,
  useCloseStockTransfer,
  useRejectStockTransfer,
  useStockTransferQuery,
} from "@/modules/inventory/presentation/api/queries";
import { TransferApproveDialog } from "@/modules/inventory/presentation/ui/transfers/TransferApproveDialog";
import { TransferCloseDialog } from "@/modules/inventory/presentation/ui/transfers/TransferCloseDialog";
import { TransferRejectDialog } from "@/modules/inventory/presentation/ui/transfers/TransferRejectDialog";
import { TransferStatusBadge } from "@/modules/inventory/presentation/ui/transfers/TransferStatusBadge";
import {
  TRANSFER_CARD,
  TRANSFER_CARD_CONTENT,
  TRANSFER_CARD_CONTENT_TABLE,
  TRANSFER_CARD_HEADER,
  TRANSFER_CARD_HEADER_TABLE,
} from "@/modules/inventory/presentation/ui/transfers/transferCardLayout";
import {
  TRANSFER_MENU_TITLE,
  transferItemTypeLabel,
} from "@/modules/inventory/presentation/ui/transfers/transferLaoLabels";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";

function MetaItem({
  icon: Icon,
  label,
  value,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-2 text-sm">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  );
}

export function TransferDetailPage() {
  const { id } = useParams({ from: "/app/inventory/transfers/$id" });
  const nav = useNavigate();
  const query = useStockTransferQuery(id);
  const approve = useApproveStockTransfer(id);
  const reject = useRejectStockTransfer(id);
  const close = useCloseStockTransfer(id);

  const transfer = query.data;
  const [qtyApproved, setQtyApproved] = useState<Record<string, number>>({});
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);

  useEffect(() => {
    if (!transfer) return;
    const next: Record<string, number> = {};
    for (const line of transfer.lines) {
      next[line.id] = line.qtyApproved ?? line.qtyRequested;
    }
    setQtyApproved(next);
  }, [transfer]);

  const buildApproveLines = (t: StockTransferDetail) =>
    t.lines.map((l) => ({
      lineId: l.id,
      qtyApproved: qtyApproved[l.id] ?? l.qtyRequested,
    }));

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: TRANSFER_MENU_TITLE, to: "/app/inventory/transfers" },
            { label: transfer?.requestNo ?? "…" },
          ]}
        />
      </Header>
      <Main>
        <QueryState result={query} title="ກຳລັງໂຫຼດ" variant="fullscreen">
          {!transfer ? null : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="font-bold font-mono text-2xl tracking-tight">
                      {transfer.requestNo}
                    </h1>
                    <TransferStatusBadge status={transfer.status} />
                  </div>
                  <p className="text-muted-foreground text-sm">
                    ສາຂາຂໍຈາກ Hub · {transfer.lines.length} ບັນທັດ
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => nav({ to: "/app/inventory/transfers" })}
                  >
                    <ArrowLeftIcon className="size-4" />
                    ກັບຄືນ
                  </Button>
                  {transfer.status === "APPROVED" && (
                    <Button
                      onClick={() =>
                        nav({
                          to: "/app/inventory/transfers/$id/ship",
                          params: { id },
                        })
                      }
                    >
                      <TruckIcon className="size-4" />
                      ໜ້າຈັດສົ່ງ
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <Card className={TRANSFER_CARD}>
                  <CardHeader className={TRANSFER_CARD_HEADER}>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Building2Icon className="size-4" />
                      ສາຂາ ແລະ Hub
                    </CardTitle>
                  </CardHeader>
                  <CardContent
                    className={`${TRANSFER_CARD_CONTENT} grid gap-4 sm:grid-cols-2`}
                  >
                    <MetaItem
                      icon={Building2Icon}
                      label="ສາຂາຂໍ"
                      value={`${transfer.fromBranchCode} — ${transfer.fromBranchName}`}
                    />
                    <MetaItem
                      icon={WarehouseIcon}
                      label="Hub"
                      value={`${transfer.toBranchCode} — ${transfer.toBranchName}`}
                    />
                  </CardContent>
                </Card>

                <Card className={TRANSFER_CARD}>
                  <CardHeader className={TRANSFER_CARD_HEADER}>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <ClockIcon className="size-4" />
                      ເວລາ ແລະ ໝາຍເຫດ
                    </CardTitle>
                  </CardHeader>
                  <CardContent
                    className={`${TRANSFER_CARD_CONTENT} grid gap-4 sm:grid-cols-2`}
                  >
                    <MetaItem
                      icon={ClockIcon}
                      label="ສົ່ງຄຳຂໍ"
                      value={
                        transfer.submittedAt
                          ? formatDateTimeLocal(new Date(transfer.submittedAt))
                          : "—"
                      }
                    />
                    <MetaItem
                      icon={TruckIcon}
                      label="ຈັດສົ່ງ"
                      value={
                        transfer.shippedAt
                          ? formatDateTimeLocal(new Date(transfer.shippedAt))
                          : "—"
                      }
                    />
                    {transfer.note ? (
                      <div className="text-sm sm:col-span-2">
                        <p className="text-muted-foreground text-xs">
                          ໝາຍເຫດສາຂາ
                        </p>
                        <p>{transfer.note}</p>
                      </div>
                    ) : null}
                    {transfer.internalNote ? (
                      <div className="text-sm sm:col-span-2">
                        <p className="text-muted-foreground text-xs">
                          ໝາຍເຫດສູນກາງ (HQ)
                        </p>
                        <p>{transfer.internalNote}</p>
                      </div>
                    ) : null}
                    {transfer.rejectReason ? (
                      <div className="text-destructive text-sm sm:col-span-2">
                        <p className="text-xs opacity-80">ເຫດຜົນປະຕິເສດ</p>
                        <p>{transfer.rejectReason}</p>
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              </div>

              <Card className={TRANSFER_CARD}>
                <CardHeader className={TRANSFER_CARD_HEADER_TABLE}>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <PackageIcon className="size-4" />
                    ລາຍການຂໍເບິກ
                  </CardTitle>
                </CardHeader>
                <CardContent className={TRANSFER_CARD_CONTENT_TABLE}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>ລາຍການ</TableHead>
                        <TableHead className="text-right">ຂໍ</TableHead>
                        <TableHead className="text-right">ອະນຸມັດ</TableHead>
                        <TableHead className="text-right">ສົ່ງ</TableHead>
                        <TableHead className="text-right">ຮັບ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transfer.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell>{line.lineNo}</TableCell>
                          <TableCell>
                            <span className="font-medium">{line.itemName}</span>
                            <span className="ml-1 text-muted-foreground text-xs">
                              ({transferItemTypeLabel(line.itemType)})
                            </span>
                            {line.toBaseFactor > 0 &&
                            line.toBaseFactor !== 1 ? (
                              <p className="text-muted-foreground text-xs">
                                1 {line.unit} = {line.toBaseFactor} ຫນ່ວຍພື້ນຖານ
                              </p>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.qtyRequested} {line.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            {transfer.status === "SUBMITTED" ? (
                              <Input
                                type="number"
                                className="ml-auto w-28 text-right tabular-nums"
                                min={0}
                                max={line.qtyRequested}
                                step="any"
                                value={
                                  qtyApproved[line.id] ?? line.qtyRequested
                                }
                                onChange={(e) =>
                                  setQtyApproved((prev) => ({
                                    ...prev,
                                    [line.id]: Number(e.target.value),
                                  }))
                                }
                              />
                            ) : (
                              <span className="tabular-nums">
                                {line.qtyApproved ?? "—"} {line.unit}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.qtyShipped == null
                              ? "—"
                              : `${line.qtyShipped} ${line.unit}`}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.qtyReceived == null
                              ? "—"
                              : `${line.qtyReceived} ${line.unit}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {transfer.status === "SUBMITTED" && (
                <Card
                  className={`${TRANSFER_CARD} border-primary/20 bg-primary/5`}
                >
                  <CardHeader className={TRANSFER_CARD_HEADER}>
                    <CardTitle className="text-base">
                      ພິຈາລະນາທີ່ສູນກາງ (HQ)
                    </CardTitle>
                  </CardHeader>
                  <CardContent
                    className={`${TRANSFER_CARD_CONTENT} flex flex-wrap gap-2`}
                  >
                    <Button onClick={() => setApproveOpen(true)}>
                      <CheckIcon className="size-4" />
                      ອະນຸມັດ
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => setRejectOpen(true)}
                    >
                      <XIcon className="size-4" />
                      ປະຕິເສດ
                    </Button>
                  </CardContent>
                </Card>
              )}

              {transfer.status === "RECEIVED" && (
                <Card className={TRANSFER_CARD}>
                  <CardHeader className={TRANSFER_CARD_HEADER}>
                    <CardTitle className="text-base">ປິດງານ</CardTitle>
                  </CardHeader>
                  <CardContent
                    className={`${TRANSFER_CARD_CONTENT} flex flex-wrap items-center justify-between gap-2`}
                  >
                    <p className="text-muted-foreground text-sm">
                      ສາຂາຮັບຄົບແລ້ວ — ປິດຂັ້ນຕອນຫຼັງຮັບຄົບ
                    </p>
                    <Button
                      isLoading={close.isPending}
                      onClick={() => setCloseOpen(true)}
                    >
                      ປິດງານ
                    </Button>
                  </CardContent>
                </Card>
              )}

              {transfer.shipments.length > 0 && (
                <Card className={TRANSFER_CARD}>
                  <CardHeader className={TRANSFER_CARD_HEADER}>
                    <CardTitle className="text-base">Lot ທີ່ສົ່ງອອກ</CardTitle>
                  </CardHeader>
                  <CardContent className={TRANSFER_CARD_CONTENT}>
                    <ul className="divide-y text-sm">
                      {transfer.shipments.map((s) => {
                        const line = transfer.lines.find(
                          (l) => l.id === s.lineId,
                        );
                        const factor =
                          line && line.toBaseFactor > 0 ? line.toBaseFactor : 1;
                        const shown = s.qty / factor;
                        return (
                          <li
                            key={s.id}
                            className="flex justify-between py-2 first:pt-0 last:pb-0"
                          >
                            <span className="font-mono">{s.sourceLotNo}</span>
                            <span className="tabular-nums">
                              {shown.toLocaleString("lo-LA", {
                                maximumFractionDigits: 2,
                              })}{" "}
                              {line?.unit ?? ""}
                              {factor !== 1 ? (
                                <span className="ml-1 text-muted-foreground text-xs">
                                  ({s.qty} ຫນ່ວຍພື້ນຖານ)
                                </span>
                              ) : null}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <TransferApproveDialog
                open={approveOpen}
                requestNo={transfer.requestNo}
                loading={approve.isPending}
                onClose={() => setApproveOpen(false)}
                onConfirm={async (internalNote) => {
                  try {
                    await approve.mutateAsync({
                      internalNote: internalNote || null,
                      lines: buildApproveLines(transfer),
                    });
                    toast.success("ອະນຸມັດຄຳຂໍແລ້ວ");
                    setApproveOpen(false);
                  } catch {
                    toast.error("ອະນຸມັດບໍ່ສຳເລັດ");
                  }
                }}
              />

              <TransferRejectDialog
                open={rejectOpen}
                requestNo={transfer.requestNo}
                loading={reject.isPending}
                onClose={() => setRejectOpen(false)}
                onConfirm={async (rejectReason) => {
                  try {
                    await reject.mutateAsync({ rejectReason });
                    toast.success("ປະຕິເສດຄຳຂໍແລ້ວ");
                    setRejectOpen(false);
                  } catch {
                    toast.error("ປະຕິເສດບໍ່ສຳເລັດ");
                  }
                }}
              />

              <TransferCloseDialog
                open={closeOpen}
                requestNo={transfer.requestNo}
                loading={close.isPending}
                onClose={() => setCloseOpen(false)}
                onConfirm={async () => {
                  try {
                    await close.mutateAsync();
                    toast.success("ປິດງານແລ້ວ");
                    setCloseOpen(false);
                  } catch {
                    toast.error("ປິດງານບໍ່ສຳເລັດ");
                  }
                }}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
